import styled, { css } from 'styled-components';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ReadOnlyBanner, useAgentPermission, useCeoWriteAccess } from '@entities/user';
import { useGetFinancierDashboardQuery } from '@shared/api';
import { riseIn } from '@shared/theme/animations';
import { IconArrowRight, IconCalculator } from '@shared/ui/icons';
import { formatMillions } from './financierFormat';
import { paymentQueue } from './paymentQueue';
import { monthLabel, moscowTodayIso } from './workingCapital/taxPeriods';

const Shell = styled.div`
  max-width: 1360px;
  width: 100%;
  min-width: 0;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
  box-sizing: border-box;
`;

const Hero = styled.section`
  position: relative;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.radius.xl};
  background: linear-gradient(
    135deg,
    rgba(16, 185, 129, 0.16) 0%,
    rgba(30, 41, 59, 0.24) 50%,
    rgba(14, 165, 233, 0.12) 100%
  );
  border: 1px solid rgba(16, 185, 129, 0.32);
  padding: ${({ theme }) => `${theme.spacing(6)} ${theme.spacing(7)}`};
  box-shadow:
    0 16px 40px -18px rgba(0, 0, 0, 0.45),
    0 0 34px -22px rgba(16, 185, 129, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  animation: ${riseIn} 0.25s ease-out;

  @media (max-width: 768px) {
    padding: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(4)}`};
  }
`;

const HeroInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(6)};
  flex-wrap: wrap;
`;

const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(4)};
  min-width: 0;
  flex: 1 1 340px;
`;

const Emblem = styled.div`
  width: 52px;
  height: 52px;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  box-shadow: 0 8px 20px -4px rgba(16, 185, 129, 0.5);
  flex-shrink: 0;
`;

const Titles = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Wordmark = styled.h1`
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};

  .accent {
    color: #10b981;
  }
`;

const Tagline = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 580px;
  line-height: 1.45;
`;

const SyncMeta = styled.div`
  margin-top: 4px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LiveDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.8);
`;

const StatStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(145px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
  width: 100%;
  min-width: 0;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, minmax(140px, 1fr));
  }

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const Chip = styled.div`
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(3.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const ChipValue = styled.div`
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
`;

const ChipLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const ChipSub = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`;

const Nav = styled.nav`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding-bottom: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  /* На телефоне — одна строка с прокруткой, как в Пульте Гендиректора */
  @media (max-width: 640px) {
    flex-wrap: nowrap;
  }
`;

const Seg = styled(Link)<{ $active?: boolean; $push?: boolean }>`
  ${({ $push }) =>
    $push &&
    css`
      margin-left: auto;
    `}
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  background: transparent;
  transition: all 0.15s ease-in-out;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.backgroundElevated};
  }

  ${({ $active }) =>
    $active &&
    css`
      color: #10b981;
      background: rgba(16, 185, 129, 0.12);
      border: 1px solid rgba(16, 185, 129, 0.35);

      &:hover {
        color: #10b981;
        background: rgba(16, 185, 129, 0.18);
      }
    `}
`;

const NavCountBadge = styled.span`
  background: #10b981;
  color: #ffffff;
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 10px;
  font-weight: 700;
`;

const CompactHero = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 44px;
  padding: 8px 16px;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: linear-gradient(
    135deg,
    rgba(16, 185, 129, 0.16) 0%,
    ${({ theme }) => (theme.name === 'dark' ? 'rgba(30, 41, 59, 0.35)' : 'rgba(30, 41, 59, 0.06)')} 50%,
    rgba(14, 165, 233, 0.12) 100%
  );
  border: 1px solid rgba(16, 185, 129, 0.32);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  width: 100%;
  min-width: 0;

  @media (max-width: 640px) {
    flex-wrap: wrap;
    max-height: none;
    padding: 8px 12px;
    gap: 8px;
  }
`;

const CompactLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
`;

const CompactTitle = styled.div`
  font-size: 15px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;

  .accent {
    color: #10b981;
  }
`;

const CompactRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  flex-wrap: wrap;
  min-width: 0;

  @media (max-width: 480px) {
    font-size: 11px;
    gap: 8px;
  }
`;

// Цвета — из темы, как в большой шапке (Hero): раньше были зашиты под тёмную
// тему (#cbd5e1 / #f8fafc), и в светлой белый текст пропадал на светлом фоне.
const CompactStat = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const CompactValue = styled.b<{ $info?: boolean }>`
  color: ${({ $info, theme }) =>
    $info ? (theme.name === 'dark' ? '#38bdf8' : '#0369a1') : theme.colors.text.primary};
`;

const CompactBadge = styled.span<{ $tone?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  background: rgba(16, 185, 129, 0.12);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.3);
`;

const Main = styled.main`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
  width: 100%;
  min-width: 0;
`;

/** Вкладки, которые пишут через API Гендиректора: право записи там — по его гарду. */
const TREASURY_SECTIONS = ['accounts', 'debts', 'inflows'];

export function FinancierLayout() {
  const location = useLocation();
  const { can_read, can_write } = useAgentPermission('finance');
  const canWriteTreasury = useCeoWriteAccess();
  const { data } = useGetFinancierDashboardQuery();

  const segments = location.pathname.split('/').filter(Boolean); // ['agents', 'finance', ...]
  const section = segments[2] ?? '';
  const sectionWrite = TREASURY_SECTIONS.includes(section) ? canWriteTreasury : can_write;

  const accSum = data?.accounts_summary;
  const paySum = data?.payment_factory;
  const queue = paySum ? paymentQueue(paySum) : null;
  const chronumSum = data?.chronum_payroll;
  const taxSum = data?.tax_summary;

  return (
    <Shell>
      {can_read && !sectionWrite && <ReadOnlyBanner />}
      {section === '' ? (
        <Hero aria-label="ОСНОВА.Финансист">
          <HeroInner>
            <Identity>
              <Emblem aria-hidden>
                <IconCalculator size={28} />
              </Emblem>
              <Titles>
                <Wordmark>
                  ОСНОВА<span className="accent">.Финансист</span>
                </Wordmark>
                <Tagline>
                  Единая фабрика платежей холдинга, 4-ступенчатый финконтроль (Договор / Первичка / БДДС / Счета ОБС),
                  шлюз с ПК «Хронум» (УРВ, табель и зарплатные ведомости) и выгрузка в 1С:Клиент-Банк.
                </Tagline>
                <SyncMeta>
                  <LiveDot />
                  <span>
                    Локальный снимок БД / Выписки 1С · Реестры и табели Хронум
                  </span>
                </SyncMeta>
              </Titles>
            </Identity>

            <StatStrip>
              <Chip>
                <ChipLabel>Свободная ликвидность</ChipLabel>
                <ChipValue>
                  {accSum ? formatMillions(accSum.free_operational_cash) : '—'}
                </ChipValue>
                <ChipSub>
                  Спецсчета ОБС: {accSum ? formatMillions(accSum.special_obs_balance) : '—'}
                </ChipSub>
              </Chip>

              <Chip
                title={
                  queue
                    ? `В работе ${queue.count} шт.: на проверке, готово для Гендиректора, у Гендиректора и согласовано — до выгрузки в банк`
                    : undefined
                }
              >
                <ChipLabel>Фабрика платежей</ChipLabel>
                <ChipValue>{queue ? formatMillions(queue.amount) : '—'}</ChipValue>
                <ChipSub>
                  Готово для Гендиректора: {paySum ? formatMillions(paySum.total_passed_gates_amount) : '—'}
                </ChipSub>
              </Chip>

              <Chip>
                <ChipLabel>Налоги к 28-му</ChipLabel>
                <ChipValue>
                  {taxSum ? formatMillions(taxSum.upcoming_tax_payment_amount) : '—'}
                </ChipValue>
                <ChipSub>
                  Остаток на счёте налогового резерва:{' '}
                  {taxSum ? formatMillions(taxSum.tax_reserve_balance) : '—'}
                </ChipSub>
              </Chip>

              <Chip>
                <ChipLabel>ФОТ Хронум</ChipLabel>
                <ChipValue>
                  {chronumSum ? formatMillions(chronumSum.net_payout_amount) : '—'}
                </ChipValue>
                <ChipSub>
                  {/* Пульт отдаёт ФОТ за текущий месяц по Москве — его и называем. */}
                  {monthLabel(moscowTodayIso())} · на смене: {chronumSum ? chronumSum.on_shift_now : 0} чел.
                </ChipSub>
              </Chip>
            </StatStrip>
          </HeroInner>
        </Hero>
      ) : (
        <CompactHero aria-label="ОСНОВА.Финансист (компактный вид)">
          <CompactLeft>
            <Emblem style={{ width: 32, height: 32, borderRadius: 8 }} aria-hidden>
              <IconCalculator size={18} />
            </Emblem>
            <CompactTitle>
              ОСНОВА<span className="accent">.Финансист</span>
            </CompactTitle>
            <CompactBadge>
              Ликвидность: {accSum ? formatMillions(accSum.free_operational_cash) : '—'}
            </CompactBadge>
          </CompactLeft>
          <CompactRight>
            <CompactStat>
              Спецсчета ОБС:{' '}
              <CompactValue $info>{accSum ? formatMillions(accSum.special_obs_balance) : '—'}</CompactValue>
            </CompactStat>
            <CompactStat>
              Готово для Гендиректора:{' '}
              <CompactValue>{paySum ? formatMillions(paySum.total_passed_gates_amount) : '—'}</CompactValue>
            </CompactStat>
          </CompactRight>
        </CompactHero>
      )}

      <Nav aria-label="Разделы модуля ОСНОВА.Финансист">
        <Seg to="/agents/finance" $active={section === ''}>
          Пульт Финдиректора и Платёжный календарь
        </Seg>
        <Seg to="/agents/finance/requests" $active={section === 'requests'}>
          Фабрика платежей и Финконтроль
          {paySum && paySum.total_requests_count > 0 && (
            <NavCountBadge>{paySum.total_requests_count}</NavCountBadge>
          )}
        </Seg>
        <Seg to="/agents/finance/accounts" $active={section === 'accounts'}>
          Счета и выписки
        </Seg>
        <Seg to="/agents/finance/debts" $active={section === 'debts'}>
          Кредиты и лизинг
        </Seg>
        <Seg to="/agents/finance/inflows" $active={section === 'inflows'}>
          Поступления (КС-3)
        </Seg>
        <Seg to="/agents/finance/counterparties" $active={section === 'counterparties'}>
          Контрагенты и 1С
        </Seg>
        <Seg to="/agents/finance/working-capital" $active={section === 'working-capital'}>
          Оборотный капитал и Налоги ЕНС
        </Seg>
        <Seg to="/agents/finance/chronum-payroll" $active={section === 'chronum-payroll'}>
          Зарплата и ФОТ (Хронум)
        </Seg>
        {/* Раздел, куда сотрудники подают счета: тот же пункт, что в главном меню. */}
        <Seg to="/pay-requests" $active={false} $push>
          Заявки на оплату
          <IconArrowRight size={14} />
        </Seg>
        <Seg to="/agents/ceo" $active={false}>
          Пульт Гендиректора
          <IconArrowRight size={14} />
        </Seg>
        <Seg to="/agents/econ" $active={false}>
          ОСНОВА.Экономист
          <IconArrowRight size={14} />
        </Seg>
      </Nav>

      <Main>
        <Outlet />
      </Main>
    </Shell>
  );
}
